// #2366 — scribe presence, announcer-fe's own DISPLAY decision.
//
// The read endpoint (#2363, GET /presence) hard-drops any row whose
// heartbeatAt is older than PRESENCE_TTL_SECONDS (60s, score-common): it
// returns LIVE rows only. That means a seat whose scribe went quiet two
// minutes ago and a seat nobody ever connected to look IDENTICAL from a
// single fresh call — both are simply absent from the response.
//
// So this card can only honestly show "gone quiet" for a seat OBSERVED
// connected earlier in THIS session: the client remembers a seat was
// present and then watches it drop out of subsequent polls, deriving
// "quiet" from wall-clock elapsed time against the last real heartbeat it
// ever saw, not from whether the row still shows up. It cannot show
// anything for a seat never observed this session (a fresh page load
// can't distinguish "was here, died 5 minutes ago" from "never
// connected") — that is a genuine API-contract gap, not a bug here (see
// card [2366](https://trello.com/c/cEApiWVj) / [2370](https://trello.com/c/VFMUBw8X)).
//
// SCRIBE_QUIET_THRESHOLD_SECONDS is a per-app judgement call (the card's own
// "Show elapsed time, not just a colour" section: "the threshold for 'gone'
// is the announcer's judgement to make in the moment"), deliberately
// SEPARATE from score-common's PRESENCE_TTL_SECONDS even though they share
// a value today — one is a storage/read cutoff, the other is a display
// urgency threshold, and nothing requires them to move together.
// Starting value from Jaybill's card comment (2026-09-07): "red at about 60
// seconds of silence while a ride is up... tighter than scoreadmin's
// because the announcer is deciding whether to call the next rider. That is
// a starting point to confirm against a real ring, not a researched figure
// — flag it for Jaybill if it feels wrong in practice."
export const SCRIBE_QUIET_THRESHOLD_SECONDS = 60;

// "12s" / "6m" / "1h 5m" — coarse on purpose, same style as OrderOfGo's own
// formatCountdown (a stopwatch reading, not a race clock). Never negative:
// a clock skew or a poll landing a beat before `now` catches up floors to 0.
export function formatElapsedSeconds(totalSeconds) {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  if (seconds < 60) return `${seconds}s`;
  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

// Folds a batch of listPresence() entries (score-common's
// toPresenceResponseEntry shape: { seat, lastSeenAt, scribeName, ... }) into
// the running "observed this session" map, keyed by seat. Pure — no I/O, no
// Date.now() — so it's fully unit-testable and the poll effect stays a thin
// caller. A seat's entry is only ever REPLACED by a fresher sighting, never
// removed here: dropping out of `entries` (the seat went quiet) is exactly
// the case this map exists to remember past the point the API stops saying
// so (see the file header). `entries` missing/malformed never throws — an
// empty poll result is a normal "nobody's live right now" answer.
export function mergeObservedPresence(prev, entries) {
  if (!Array.isArray(entries) || entries.length === 0) return prev;
  let next = prev;
  for (const entry of entries) {
    if (!entry?.seat || !entry.lastSeenAt) continue;
    const lastSeenAtMs = new Date(entry.lastSeenAt).getTime();
    if (!Number.isFinite(lastSeenAtMs)) continue;
    const existing = prev[entry.seat];
    if (existing && existing.lastSeenAtMs >= lastSeenAtMs) continue;
    if (next === prev) next = { ...prev };
    next[entry.seat] = { lastSeenAtMs, scribeName: entry.scribeName ?? null };
  }
  return next;
}

// The render-ready rows for the current class's presence strip: one per
// OBSERVED seat (never a seat this session has not seen — see file header),
// sorted for a stable on-screen order, each carrying the elapsed seconds
// AND the derived "quiet" boolean so a caller never has to duplicate the
// threshold comparison (AC6 — one named constant, one place it's applied).
export function presenceRowsFor(observed, nowMs) {
  return Object.entries(observed || {})
    .map(([seat, { lastSeenAtMs }]) => {
      const elapsedSeconds = Math.max(0, Math.floor((nowMs - lastSeenAtMs) / 1000));
      return {
        seat,
        elapsedSeconds,
        quiet: elapsedSeconds >= SCRIBE_QUIET_THRESHOLD_SECONDS,
      };
    })
    .sort((a, b) => a.seat.localeCompare(b.seat));
}
